import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { DataCardShell } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import {
  formatLanguage,
  formatProjectedDates,
  statusChip,
  typeChip,
} from "../../lifecycle/lifecycleUi";
import {
  projectedSupportParts,
  retreatDisplayTitle,
  retreatLocationLine,
} from "../manageRetreatTypes";
import { SectionActionButtons } from "../SectionActionButtons";
import { dashboardContainedButtonSx } from "../dashboardUi";

export function ProjectedRetreatCard({
  r,
  isSuperAdmin,
  onHistory,
  onDelete,
  onApprove,
  onRestore,
}: {
  r: LifecycleRetreat;
  isSuperAdmin: boolean;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onApprove: (r: LifecycleRetreat) => void;
  onRestore: (r: LifecycleRetreat) => void;
}) {
  const navigate = useNavigate();
  const { typeLabel, couple } = projectedSupportParts(r);
  const support =
    [typeLabel, couple].filter(Boolean).join(" · ") || "Unassigned";
  const percent = r.readinessPercent || 0;

  return (
    <DataCardShell
      title={retreatDisplayTitle(r)}
      subtitle={retreatLocationLine(r) || undefined}
      chips={
        <>
          {statusChip(r.lifecycleStatus || "Projected")}
          {typeChip(r.retreatType)}
        </>
      }
      fields={[
        { label: "Season / Year", value: r.seasonLabel || "—" },
        { label: "Language", value: formatLanguage(r.language) },
        {
          label: "Lead Couple",
          value: r.leadCoupleName || "Not assigned",
        },
        { label: "Planning Support", value: support },
        {
          label: "Dates",
          value: formatProjectedDates(r.dateFrom, r.dateTo),
        },
        {
          label: "Planning Readiness",
          value: (
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={700}>
                {percent}% · Planning started
              </Typography>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{ height: 8, borderRadius: 10 }}
              />
            </Stack>
          ),
        },
      ]}
      primaryAction={
        <Button
          size="small"
          variant="contained"
          onClick={() => navigate(`/retreats/${r.id}/project`)}
          sx={{ width: { xs: "100%", sm: "auto" }, ...dashboardContainedButtonSx }}
        >
          Open Projected
        </Button>
      }
      secondaryActions={
        <SectionActionButtons
          r={r}
          kind="projected"
          isSuperAdmin={isSuperAdmin}
          onHistory={onHistory}
          onDelete={onDelete}
          onApprove={onApprove}
          onRestore={onRestore}
        />
      }
    />
  );
}
